import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup } from 'reactstrap';
import moment from 'moment';
import { collection, getDocs } from 'firebase/firestore';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import { withStyles, createStyles, WithStyles } from '@material-ui/core/styles';
import { useCollectionCache } from '../../services/collectionCache.service';
import { useOrganizationStore } from '../../store/organization.store';
import { SELECTABLE_DURATIONS, calculateExpiryDate } from '../../constants/licenseDurations';
import {
  LicenseDocument,
  LicenseDurationType,
  generateClaimCode,
} from '../../types/license';

// Styles matching AddAudioForm pattern
const styles = () => createStyles({
  autocomplete: {
    width: '100%',
    '& .MuiInputBase-root': {
      backgroundColor: 'transparent',
      color: '#fff',
      minHeight: '32px',
      fontFamily: 'var(--font-family-primary)',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
        borderWidth: '1px'
      },
      '& .MuiAutocomplete-tag': {
        backgroundColor: 'var(--accent-1-color)',
        borderRadius: '8px',
        margin: '2px',
        padding: '4px 8px',
        color: '#fff',
        fontSize: 'var(--menu-font-size)',
        fontFamily: 'var(--font-family-primary)',
        '& .MuiChip-label': {
          padding: '0 4px'
        },
        '& .MuiChip-deleteIcon': {
          color: '#fff',
          fontSize: '16px',
          margin: '0 2px 0 -4px'
        }
      }
    },
    '& input': {
      padding: '4px 8px',
      fontSize: 'var(--menu-font-size)',
      fontFamily: 'var(--font-family-primary)',
      color: '#fff'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.1)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.2)'
    },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
      color: '#ffffff',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)'
      }
    }
  },
  chip: {
    backgroundColor: 'var(--accent-1-color)',
    color: '#fff',
    margin: '2px',
    height: '24px',
    fontFamily: 'var(--font-family-primary)',
    '& .MuiChip-label': {
      padding: '0 6px',
      fontSize: '0.75rem',
      fontFamily: 'var(--font-family-primary)'
    },
    '& .MuiChip-deleteIcon': {
      color: '#fff',
      fontSize: '16px'
    }
  }
});

// Share target interface
export interface ShareTarget {
  type: 'org' | 'client' | 'agency';
  id: string;
  name: string;
  linkedOrgId: string | null;
  hasOrg: boolean;
}

// Extended license data including share info
export interface LicenseDataWithShare extends Partial<LicenseDocument> {
  shareTarget?: ShareTarget | null;
}

interface ShareLicenseModalProps extends WithStyles<typeof styles> {
  isOpen: boolean;
  onClose: () => void;
  onApply: (licenseData: LicenseDataWithShare) => void;
  trackName: string;
  trackData?: any;
  firebase: any;
}

interface SelectOption {
  label: string;
  value: string;
}

const ShareLicenseModal: React.FC<ShareLicenseModalProps> = ({
  isOpen,
  onClose,
  onApply,
  trackName,
  trackData,
  firebase,
  classes,
}) => {
  // Duration state
  const [selectedDuration, setSelectedDuration] = useState<LicenseDurationType | null>(null);
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [customDate, setCustomDate] = useState('');

  // License metadata state
  const [jobTitle, setJobTitle] = useState('');
  const [client, setClient] = useState<SelectOption | null>(null);
  const [agency, setAgency] = useState<SelectOption | null>(null);
  const [producer, setProducer] = useState<SelectOption | null>(null);
  const [creativeDirectors, setCreativeDirectors] = useState<SelectOption[]>([]);
  const [flightingPlatform, setFlightingPlatform] = useState<SelectOption[]>([]);
  const [territories, setTerritories] = useState<SelectOption[]>([]);
  const [notes, setNotes] = useState('');

  // Exclusive license state
  const [isExclusive, setIsExclusive] = useState(false);
  const [hasActiveExclusive, setHasActiveExclusive] = useState(false);
  const [checkingExclusive, setCheckingExclusive] = useState(false);

  // Share target state
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);
  const [shareTargetSearch, setShareTargetSearch] = useState('');
  const [shareTargetOptions, setShareTargetOptions] = useState<ShareTarget[]>([]);
  const [isSearchingTargets, setIsSearchingTargets] = useState(false);

  // Options from collections
  const [clientOptions, setClientOptions] = useState<SelectOption[]>([]);
  const [agencyOptions, setAgencyOptions] = useState<SelectOption[]>([]);
  const [producerOptions, setProducerOptions] = useState<SelectOption[]>([]);
  const [creativeDirectorOptions, setCreativeDirectorOptions] = useState<SelectOption[]>([]);
  const [flightingOptions, setFlightingOptions] = useState<SelectOption[]>([]);
  const [territoryOptions, setTerritoryOptions] = useState<SelectOption[]>([]);

  // Loading state
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  // Get cached collections
  const collectionCache = useCollectionCache();
  const { currentOrgId } = useOrganizationStore();

  // Debounced search for share targets
  const searchShareTargets = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm || searchTerm.length < 2 || !firebase?.searchShareTargets) {
        setShareTargetOptions([]);
        return;
      }

      setIsSearchingTargets(true);
      try {
        const results = await firebase.searchShareTargets(searchTerm, currentOrgId);
        setShareTargetOptions(results);
      } catch (error) {
        console.error('Error searching share targets:', error);
        setShareTargetOptions([]);
      }
      setIsSearchingTargets(false);
    },
    [firebase, currentOrgId]
  );

  // Debounce the search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchShareTargets(shareTargetSearch);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [shareTargetSearch, searchShareTargets]);

  // Load options from collections
  useEffect(() => {
    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        // Helper to load from cache or fallback to Firestore
        const loadGlobalCollection = async (
          cacheData: any[],
          collectionName: string,
          setOptions: (opts: SelectOption[]) => void
        ) => {
          if (cacheData.length > 0) {
            // Use item.value (from Firestore document) for consistency with AddAudioForm
            // item.id is the document ID, item.value should equal item.id if formatted correctly
            setOptions(cacheData.map(item => ({ label: item.displayName, value: item.value || item.id })));
          } else if (firebase?.db) {
            // Fallback: load directly from Firestore
            try {
              const snapshot = await getDocs(collection(firebase.db, collectionName));
              const opts = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  label: data.label || data.name || data.displayName || doc.id,
                  // Use data.value for consistency with AddAudioForm
                  // Fallback to doc.id if value is not set
                  value: data.value || doc.id,
                };
              });
              // Sort alphabetically
              opts.sort((a, b) => a.label.localeCompare(b.label));
              setOptions(opts);
            } catch (err) {
              console.error(`Error loading ${collectionName}:`, err);
            }
          }
        };

        // Load global collections (with fallback to Firestore)
        await Promise.all([
          loadGlobalCollection(collectionCache.client, 'client', setClientOptions),
          loadGlobalCollection(collectionCache.agency, 'agency', setAgencyOptions),
          loadGlobalCollection(collectionCache.territories, 'territories', setTerritoryOptions),
          loadGlobalCollection(collectionCache.flightingplatform, 'flightingplatform', setFlightingOptions),
        ]);

        // Load org-scoped collections from cache (no fallback needed)
        // Use value || id for consistency with AddAudioForm
        if (collectionCache.producer.length > 0) {
          setProducerOptions(collectionCache.producer.map(p => ({ label: p.displayName, value: p.value || p.id })));
        }
        if (collectionCache.creativedirector.length > 0) {
          setCreativeDirectorOptions(collectionCache.creativedirector.map(c => ({ label: c.displayName, value: c.value || c.id })));
        }
      } catch (error) {
        console.error('Error loading options:', error);
      }
      setIsLoadingOptions(false);
    };

    if (isOpen) {
      loadOptions();
    }
  }, [isOpen, firebase, collectionCache, currentOrgId]);

  // Pre-fill from track data
  useEffect(() => {
    if (trackData && isOpen) {
      if (trackData.campaign || trackData.jobTitle) {
        setJobTitle(trackData.campaign || trackData.jobTitle || '');
      }
      if (trackData.client) {
        setClient(trackData.client);
      }
      if (trackData.agency) {
        setAgency(trackData.agency);
      }
      if (trackData.producer) {
        setProducer(typeof trackData.producer === 'string'
          ? { label: trackData.producer, value: trackData.producer }
          : trackData.producer
        );
      }
      if (trackData.creativedirector || trackData.creativeDirector) {
        const cd = trackData.creativedirector || trackData.creativeDirector;
        if (Array.isArray(cd)) {
          setCreativeDirectors(cd);
        } else if (cd) {
          setCreativeDirectors([typeof cd === 'string' ? { label: cd, value: cd } : cd]);
        }
      }
      if (trackData.flightingplatform || trackData.flightingPlatform) {
        const fp = trackData.flightingplatform || trackData.flightingPlatform;
        if (Array.isArray(fp)) {
          setFlightingPlatform(fp);
        }
      }
      // Pre-fill dateExpires as custom duration
      if (trackData.dateExpires) {
        const expiryTimestamp = typeof trackData.dateExpires === 'number'
          ? trackData.dateExpires
          : new Date(trackData.dateExpires).getTime();
        // Format as DD/MM/YYYY for the date input (with slashes for parsing)
        const formattedDate = moment(expiryTimestamp).format('DD/MM/YYYY');
        setShowCustomDate(true);
        setCustomDate(formattedDate);
        setSelectedDuration(null); // Clear any preset duration since we're using custom
      }
    }
  }, [trackData, isOpen]);

  // Check for active exclusive license when modal opens
  useEffect(() => {
    const checkExclusiveLicense = async () => {
      if (!isOpen || !firebase || !currentOrgId || !trackData?.id) {
        setHasActiveExclusive(false);
        return;
      }

      setCheckingExclusive(true);
      try {
        const activeLicense = await firebase.getActiveExclusiveLicense(currentOrgId, trackData.id);
        setHasActiveExclusive(!!activeLicense);
      } catch (error) {
        console.error('Error checking exclusive license:', error);
        setHasActiveExclusive(false);
      } finally {
        setCheckingExclusive(false);
      }
    };

    checkExclusiveLicense();
  }, [isOpen, firebase, currentOrgId, trackData?.id]);

  // Reset state when modal closes
  const resetState = () => {
    setSelectedDuration(null);
    setShowCustomDate(false);
    setCustomDate('');
    setJobTitle('');
    setClient(null);
    setAgency(null);
    setProducer(null);
    setCreativeDirectors([]);
    setFlightingPlatform([]);
    setTerritories([]);
    setNotes('');
    setShareTarget(null);
    setShareTargetSearch('');
    setShareTargetOptions([]);
    setIsExclusive(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Duration selection
  const selectDuration = (durationType: LicenseDurationType | 'custom') => {
    if (durationType === 'custom') {
      setShowCustomDate(true);
      setSelectedDuration(null);
    } else {
      setShowCustomDate(false);
      setCustomDate('');
      setSelectedDuration(durationType);
    }
  };

  // Custom date handling
  const handleCustomDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^\d]/g, '');
    if (value.length > 2) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    }
    if (value.length > 5) {
      value = value.slice(0, 5) + '/' + value.slice(5);
    }
    if (value.length > 10) {
      value = value.slice(0, 10);
    }
    setCustomDate(value);
    setSelectedDuration(null);
  };

  const isValidCustomDate = (dateStr: string): boolean => {
    if (!dateStr || dateStr.length !== 10) return false;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return false;

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
    if (year < 2000 || year > 2099) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    const date = moment(`${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`, 'YYYY-MM-DD');
    return date.isValid() && date.isSameOrAfter(moment(), 'day');
  };

  // Get share target display label
  const getShareTargetLabel = (target: ShareTarget) => {
    const typeLabel = target.type === 'org' ? 'Org' : target.type === 'client' ? 'Client' : 'Agency';
    const statusLabel = target.hasOrg ? 'Direct share' : 'Creates claim link';
    return `${target.name} (${typeLabel}) - ${statusLabel}`;
  };

  // Apply license
  const handleApply = () => {
    let dateExpires: number | null = null;
    let licenseType: LicenseDurationType = 'none';

    if (showCustomDate && customDate && isValidCustomDate(customDate)) {
      const parts = customDate.split('/');
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      dateExpires = moment(`${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`, 'YYYY-MM-DD').endOf('day').valueOf();
      licenseType = 'custom';
    } else if (selectedDuration) {
      licenseType = selectedDuration;
      dateExpires = calculateExpiryDate(selectedDuration);
    }

    // Build license data
    const licenseData: LicenseDataWithShare = {
      licenseType,
      dateStart: Date.now(),
      dateExpires,
      jobTitle,
      client: client ? { label: client.label, value: client.value } : null,
      agency: agency ? { label: agency.label, value: agency.value } : null,
      producer: producer ? { label: producer.label, value: producer.value } : null,
      creativeDirectors: creativeDirectors.map(cd => ({ label: cd.label, value: cd.value })),
      flightingPlatform: flightingPlatform.map(fp => ({ label: fp.label, value: fp.value })),
      territories: territories.map(t => ({ label: t.label, value: t.value })),
      shareTarget: shareTarget || null,
      isExclusive,
    };

    // Only include notes if it has a value (Firestore doesn't accept undefined)
    if (notes) {
      licenseData.notes = notes;
    }

    // Set status and sharing fields based on share target
    if (shareTarget) {
      if (shareTarget.hasOrg && shareTarget.linkedOrgId) {
        // Direct share to existing org - status is 'pending' until client claims
        licenseData.clientOrgId = shareTarget.linkedOrgId;
        licenseData.status = 'pending';
        licenseData.sharedAt = Date.now();
      } else {
        // Generate claim code for org-less target
        licenseData.claimCode = generateClaimCode();
        licenseData.status = 'pending';
        licenseData.sharedAt = Date.now();
      }
    } else {
      // No share target - unshared license (for internal tracking only)
      licenseData.status = 'active';
      licenseData.clientOrgId = null;
      licenseData.claimCode = null;
    }

    onApply(licenseData);
    resetState();
  };

  const isApplyDisabled = !selectedDuration && !(showCustomDate && isValidCustomDate(customDate));

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="lg"
      style={{
        color: '#ffffff',
        maxWidth: '600px',
        margin: '1.75rem auto',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
        border: 'none',
        outline: 'none',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleClose}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          fontSize: '1.25rem',
          fontWeight: '500',
        }}
        close={
          <button
            className="close"
            onClick={handleClose}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              padding: '0.25rem',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>Create License</span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          fontSize: '1rem',
          lineHeight: '1.5',
          maxHeight: '70vh',
          overflowY: 'auto',
        }}
      >
        <p style={{ color: '#fff', marginBottom: '1rem' }}>
          Create license for "<strong>{trackName}</strong>"
        </p>

        {/* Duration Selection */}
        <div style={{ marginBottom: '1.5rem' }}>
          <Label style={{ color: '#fff', fontWeight: '500', marginBottom: '0.5rem' }}>
            License Duration <span style={{ color: 'var(--accent-1-color)' }}>*</span>
          </Label>
          <div className="d-flex flex-wrap" style={{ gap: '0.5rem' }}>
            {SELECTABLE_DURATIONS.map((option) => (
              <Button
                key={option.value}
                size="sm"
                className={selectedDuration === option.value ? 'btn-success-custom' : 'btn-primary-custom'}
                onClick={() => selectDuration(option.value)}
                style={{ position: 'relative', minWidth: '80px' }}
              >
                <span style={{ position: 'relative', zIndex: 3, color: '#ffffff' }}>{option.label}</span>
              </Button>
            ))}
            <Button
              size="sm"
              className={showCustomDate ? 'btn-success-custom' : 'btn-primary-custom'}
              onClick={() => selectDuration('custom')}
              style={{ position: 'relative', minWidth: '80px' }}
            >
              <span style={{ position: 'relative', zIndex: 3, color: '#ffffff' }}>Custom</span>
            </Button>
          </div>
          {showCustomDate && (
            <div className="mt-2">
              <Input
                type="text"
                value={customDate}
                onChange={handleCustomDateChange}
                placeholder="dd/mm/yyyy"
                maxLength={10}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: `1px solid ${customDate && !isValidCustomDate(customDate) && customDate.length === 10 ? 'var(--accent-1-color)' : 'rgba(255, 255, 255, 0.2)'}`,
                  color: '#fff',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  maxWidth: '150px',
                }}
              />
              {customDate && customDate.length === 10 && !isValidCustomDate(customDate) && (
                <small style={{ color: 'var(--accent-1-color)', marginTop: '4px', display: 'block' }}>
                  Please enter a valid future date
                </small>
              )}
            </div>
          )}
        </div>

        {/* Exclusive License Section */}
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <input
              type="checkbox"
              id="exclusive-license"
              checked={isExclusive}
              onChange={(e) => setIsExclusive(e.target.checked)}
              disabled={hasActiveExclusive || checkingExclusive}
              style={{
                width: '18px',
                height: '18px',
                cursor: hasActiveExclusive ? 'not-allowed' : 'pointer',
                accentColor: 'var(--accent-1-color)',
              }}
            />
            <label
              htmlFor="exclusive-license"
              style={{
                color: hasActiveExclusive ? 'rgba(255, 255, 255, 0.5)' : '#fff',
                fontWeight: '500',
                cursor: hasActiveExclusive ? 'not-allowed' : 'pointer',
                marginBottom: 0,
              }}
            >
              Exclusive License
            </label>
            {checkingExclusive && (
              <span className="spinner-border spinner-border-sm" style={{ color: 'var(--accent-1-color)' }} />
            )}
          </div>
          <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', marginTop: '0.5rem', marginBottom: 0 }}>
            {hasActiveExclusive
              ? 'This track already has an active exclusive license. A new exclusive license cannot be created until it expires.'
              : 'When enabled, this track will be hidden from the public catalogue until the license expires or is revoked.'
            }
          </p>
        </div>

        {/* Share With Section */}
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Label style={{ color: '#fff', fontWeight: '500', marginBottom: '0.5rem' }}>
            Share With <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 'normal' }}>(optional)</span>
          </Label>
          <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            Search for an organization, client, or agency to share this license with.
          </p>

          {shareTarget ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.75rem',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
            }}>
              <div>
                <div style={{ fontWeight: '500' }}>{shareTarget.name}</div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                  {shareTarget.type === 'org' ? 'Organization' : shareTarget.type === 'client' ? 'Client' : 'Agency'}
                  {' - '}
                  {shareTarget.hasOrg ? (
                    <span style={{ color: 'var(--success-color, #28a745)' }}>Will share directly</span>
                  ) : (
                    <span style={{ color: 'var(--accent-1-color)' }}>Will create claim link</span>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                color="link"
                onClick={() => {
                  setShareTarget(null);
                  setShareTargetSearch('');
                }}
                style={{ color: 'var(--accent-1-color)', padding: '0.25rem 0.5rem' }}
              >
                <i className="tim-icons icon-simple-remove"></i>
              </Button>
            </div>
          ) : (
            <Autocomplete
              options={shareTargetOptions}
              getOptionLabel={(option) => getShareTargetLabel(option)}
              inputValue={shareTargetSearch}
              onInputChange={(_, newValue) => setShareTargetSearch(newValue)}
              onChange={(_, newValue) => {
                setShareTarget(newValue);
                setShareTargetSearch('');
              }}
              loading={isSearchingTargets}
              noOptionsText={shareTargetSearch.length < 2 ? "Type to search..." : "No matches found"}
              className={classes.autocomplete}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Search organizations, clients, or agencies..."
                  size="small"
                />
              )}
              renderOption={(option) => (
                <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                  <div style={{ fontWeight: '500' }}>{option.name}</div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                    {option.type === 'org' ? 'Organization' : option.type === 'client' ? 'Client' : 'Agency'}
                    {' - '}
                    {option.hasOrg ? 'Direct share' : 'Creates claim link'}
                  </div>
                </div>
              )}
            />
          )}
        </div>

        {/* Job Title */}
        <FormGroup style={{ marginBottom: '1rem' }}>
          <Label style={{ color: '#fff', fontWeight: '500' }}>Job Title / Campaign</Label>
          <Input
            type="text"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g., Summer Campaign 2025"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#fff',
              borderRadius: '4px',
            }}
          />
        </FormGroup>

        {/* Client & Agency Row */}
        <div className="d-flex" style={{ gap: '1rem', marginBottom: '1rem' }}>
          <FormGroup style={{ flex: 1 }}>
            <Label style={{ color: '#fff', fontWeight: '500' }}>Client</Label>
            <Autocomplete
              freeSolo
              options={clientOptions}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
              value={client}
              onChange={(_, newValue) => {
                if (typeof newValue === 'string') {
                  setClient({ label: newValue, value: newValue });
                } else {
                  setClient(newValue);
                }
              }}
              loading={isLoadingOptions}
              className={classes.autocomplete}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select or type..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={index}
                    label={typeof option === 'string' ? option : option.label}
                    {...getTagProps({ index })}
                    className={classes.chip}
                  />
                ))
              }
            />
          </FormGroup>
          <FormGroup style={{ flex: 1 }}>
            <Label style={{ color: '#fff', fontWeight: '500' }}>Agency</Label>
            <Autocomplete
              freeSolo
              options={agencyOptions}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
              value={agency}
              onChange={(_, newValue) => {
                if (typeof newValue === 'string') {
                  setAgency({ label: newValue, value: newValue });
                } else {
                  setAgency(newValue);
                }
              }}
              loading={isLoadingOptions}
              className={classes.autocomplete}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select or type..."
                  size="small"
                />
              )}
            />
          </FormGroup>
        </div>

        {/* Producer & Creative Directors Row */}
        <div className="d-flex" style={{ gap: '1rem', marginBottom: '1rem' }}>
          <FormGroup style={{ flex: 1 }}>
            <Label style={{ color: '#fff', fontWeight: '500' }}>Producer</Label>
            <Autocomplete
              freeSolo
              options={producerOptions}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
              value={producer}
              onChange={(_, newValue) => {
                if (typeof newValue === 'string') {
                  setProducer({ label: newValue, value: newValue });
                } else {
                  setProducer(newValue);
                }
              }}
              loading={isLoadingOptions}
              className={classes.autocomplete}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select or type..."
                  size="small"
                />
              )}
            />
          </FormGroup>
          <FormGroup style={{ flex: 1 }}>
            <Label style={{ color: '#fff', fontWeight: '500' }}>Creative Directors</Label>
            <Autocomplete
              multiple
              freeSolo
              options={creativeDirectorOptions}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
              value={creativeDirectors}
              onChange={(_, newValue) => {
                const mapped = newValue.map(v =>
                  typeof v === 'string' ? { label: v, value: v } : v
                );
                setCreativeDirectors(mapped);
              }}
              loading={isLoadingOptions}
              className={classes.autocomplete}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select or type..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={index}
                    label={typeof option === 'string' ? option : option.label}
                    {...getTagProps({ index })}
                    className={classes.chip}
                  />
                ))
              }
            />
          </FormGroup>
        </div>

        {/* Flighting Platform */}
        <FormGroup style={{ marginBottom: '1rem' }}>
          <Label style={{ color: '#fff', fontWeight: '500' }}>Flighting Platform</Label>
          <Autocomplete
            multiple
            options={flightingOptions}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
            value={flightingPlatform}
            onChange={(_, newValue) => {
              const mapped = newValue.map(v =>
                typeof v === 'string' ? { label: v, value: v } : v
              );
              setFlightingPlatform(mapped);
            }}
            loading={isLoadingOptions}
            className={classes.autocomplete}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select platforms..."
                size="small"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  label={typeof option === 'string' ? option : option.label}
                  {...getTagProps({ index })}
                  className={classes.chip}
                />
              ))
            }
          />
        </FormGroup>

        {/* Territories */}
        <FormGroup style={{ marginBottom: '1rem' }}>
          <Label style={{ color: '#fff', fontWeight: '500' }}>Territories</Label>
          <Autocomplete
            multiple
            options={territoryOptions}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
            value={territories}
            onChange={(_, newValue) => {
              const mapped = newValue.map(v =>
                typeof v === 'string' ? { label: v, value: v } : v
              );
              setTerritories(mapped);
            }}
            loading={isLoadingOptions}
            className={classes.autocomplete}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select territories..."
                size="small"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  label={typeof option === 'string' ? option : option.label}
                  {...getTagProps({ index })}
                  className={classes.chip}
                />
              ))
            }
          />
        </FormGroup>

        {/* Notes */}
        <FormGroup style={{ marginBottom: '0' }}>
          <Label style={{ color: '#fff', fontWeight: '500' }}>Notes</Label>
          <Input
            type="textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes..."
            rows={3}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#fff',
              borderRadius: '4px',
              resize: 'vertical',
            }}
          />
        </FormGroup>
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '1rem',
        }}
      >
        <Button
          className="btn-info-custom"
          onClick={handleApply}
          disabled={isApplyDisabled}
          style={isApplyDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>
            {shareTarget ? (shareTarget.hasOrg ? 'Create & Share' : 'Create & Get Link') : 'Create License'}
          </span>
        </Button>
        <Button className="btn-danger-custom" onClick={handleClose}>
          <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default withStyles(styles)(ShareLicenseModal);
